import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Download } from "lucide-react";
import { StoreLayout } from "@/components/StoreLayout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api, formatINR, resolveImg, downloadBlob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUS_STYLES = {
  pending_approval: "bg-[#FBF1DF] text-[#7A4B12] border border-[#E7D0A8]",
  approved: "bg-[#EAF1F7] text-[#1E3E5C] border border-[#C7D7E6]",
  processing: "bg-[#E7F3EA] text-[#1F5B2E] border border-[#BFDCC7]",
  payment_confirmed: "bg-[#E7F3EA] text-[#1F5B2E] border border-[#BFDCC7]",
  shipped: "bg-[#EDE7F7] text-[#4B2E7A] border border-[#D0C7E7]",
  completed: "bg-[#E7F3EA] text-[#1F5B2E] border border-[#BFDCC7]",
  rejected: "bg-[#FBE7E7] text-[#7A1F1F] border border-[#E7B9B9]",
  cancelled: "bg-[#F0E9E9] text-[#5A4A4A] border border-[#DDD0D0]",
};
const STATUS_LABELS = {
  pending_approval: "Pending Approval", approved: "Approved", processing: "Processing",
  payment_confirmed: "Processing", shipped: "Shipped", completed: "Completed", rejected: "Rejected", cancelled: "Cancelled",
};

export default function Orders() {
  const navigate = useNavigate();
  const { shopLoggedIn } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!shopLoggedIn) { navigate("/login"); return; }
    api.get("/orders").then(({ data }) => setOrders(data)).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  return (
    <StoreLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display text-3xl mb-6">My Orders</h1>
        {loading ? <div className="py-24 text-center text-muted-foreground">Loading...</div>
          : orders.length === 0 ? (
          <div className="py-24 text-center">
            <Package className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
            <p className="text-muted-foreground">No orders yet.</p>
            <Button className="mt-4" onClick={() => navigate("/products")}>Start shopping</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((o) => (
              <Card key={o.id} data-testid="order-card" className="p-5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-mono font-medium">{o.order_no}</div>
                    <div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString("en-IN")}</div>
                  </div>
                  <Badge className={STATUS_STYLES[o.status] || ""}>{STATUS_LABELS[o.status] || o.status}</Badge>
                </div>
                <div className="flex gap-2 mt-4 flex-wrap">
                  {o.items.map((it, i) => (
                    <img key={`${it.image || it.name}-${i}`} src={resolveImg(it.image)} alt={it.name} className="h-14 w-14 rounded-lg object-cover border border-border" title={`${it.name} x${it.quantity}`} />
                  ))}
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                  <span className="text-sm text-muted-foreground">{o.items.length} item(s)</span>
                  <div className="flex items-center gap-3">
                    <Button data-testid="order-download-pdf" size="sm" variant="outline" className="gap-1.5"
                      onClick={() => downloadBlob(`/orders/${o.id}/invoice.pdf`, `Invoice-${o.order_no}.pdf`).catch(() => toast.error("Download failed"))}>
                      <Download className="h-3.5 w-3.5" /> Invoice PDF
                    </Button>
                    <span className="font-mono text-[hsl(var(--primary))]">{formatINR(o.total)}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </StoreLayout>
  );
}
